import { useState } from "react";
import { ModifierOption } from "../types";
import TextField from "../components/common/TextField";
import useDebounce from "../hooks/useDebounce";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import {
  useDeleteModifierOptionMutation,
  useFetchModifierOptionsQuery,
  useToggleModifierOptionStockMutation,
  useTypedSelector,
} from "../store";
import { Link } from "react-router-dom";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import ToggleSwitch from "../components/common/ToggleSwitch";
import Pagination from "../components/common/Pagination";
import { showToast, ToastType } from "../utils/toast.utils";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import { centeredTdClasses, tdClasses, thClasses } from "../configs";

const ModifierOptionsWrapper = () => {
  const restaurant = useTypedSelector((state) => state.session.restaurant);

  if (!restaurant)
    return (
      <div className="w-full flex justify-center mt-4 text-xl font-semibold">
        Veuillez sélectionner un restaurant
      </div>
    );

  return (
    <div className="w-full flex flex-col items-center justify-center">
      <ModifierOptions restaurantId={restaurant._id} />
    </div>
  );
};

interface ModifierOptionsProps {
  restaurantId: string;
}

const ModifierOptions: React.FC<ModifierOptionsProps> = ({ restaurantId }) => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modifierToDelete, setModifierToDelete] =
    useState<ModifierOption | null>(null);

  const [toggleStock] = useToggleModifierOptionStockMutation();
  const [deleteModifierOption] = useDeleteModifierOptionMutation();

  const confirmDelete = (modifierToDelete: ModifierOption) => {
    setModifierToDelete(modifierToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteModifierOption = async () => {
    if (!modifierToDelete) return;
    const result = await deleteModifierOption(modifierToDelete._id);
    if (!result.error) {
      showToast("Supplément supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setModifierToDelete(null);
  };

  const { data: modifierOptions } = useFetchModifierOptionsQuery({
    restaurantId,
    searchQuery: {
      page,
      perPage,
      displayName: debouncedDislayName,
    },
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="flex items-center justify-between">
        <div className="w-1/4">
          <TextField
            id="lastName"
            value={displayName}
            onChange={(e) =>
              setDisplayName((e.target as HTMLInputElement).value)
            }
            placeholder="Nom supplément"
          />
        </div>
        <NavigationLinkBtn
          to="/modifier-options/create"
          label="Créer un supplément"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-4/12" />
          <col className="w-3/12" />
          <col className="w-3/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th key={0} className={thClasses}>
              Nom
            </th>
            <th key={1} className={thClasses}>
              Prix
            </th>
            <th key={2} className={thClasses}>
              Stock
            </th>
            <th key={3} className={thClasses}>
              Modifier
            </th>
            <th key={4} className={thClasses}>
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {modifierOptions?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.displayName} </td>
                <td className={centeredTdClasses}> {row.price} </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <ToggleSwitch
                      isOn={row.haveStock}
                      onToggle={() => {
                        toggleStock({
                          modifierOptionId: row._id,
                          haveStock: !row.haveStock,
                        });
                      }}
                    />
                  </div>
                </td>
                <td className={tdClasses}>
                  <Link className="w-full flex justify-center" to={row._id}>
                    <FaEdit className="w-8 text-primary" />{" "}
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: modifierOptions?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteModifierOption}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer {modifierToDelete?.displayName} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ModifierOptionsWrapper;
